import Dexie from "dexie";
import { apiBaseUrl } from "./Globals";

// Create a new database
export const db = new Dexie("BiathlonHubDB");

// Define schema (similar to tables in SQL)
// oid - server version of this row
db.version(1).stores({
    settings: "key, value",
    athletes: "++id, oid, crdate, tstmp, nickname, firstname, lastname, email, gender, height, weight", // ++id = auto-increment
    shooting: "++id, oid, crdate, tstmp, date, athlete, event, position, type, points, correctionHorizontal, correctionVertical, note",
    event: "++id, oid, crdate, tstmp, title, type",
    event_athletes: "++id, oid, athlete, event, bib, eventNote",
    deletedItems: "++id, table, oid, deletedAt"
});

export const cleanAllTables = async () => {
    db.settings.clear();
    db.athletes.clear();
    db.shooting.clear();
    db.event.clear();
    db.event_athletes.clear();
    db.deletedItems.clear();
}


// Function to add an athlete
export const addAthlete = async (athlete) => {
    const timestamp = Math.round(Date.now() / 1000);

    if (!athlete.crdate) {
        athlete.crdate = timestamp
    }

    athlete.tstmp = timestamp

    const id = await db.athletes.add(athlete);
    return id;
};

export const updateAthlete = async (id, updatedData) => {
    const timestamp = Math.round(Date.now() / 1000);
    updatedData.tstmp = timestamp
    return await db.athletes.update(Number(id), updatedData);
};

// Athletes
export const getAthletes = async () => {
    return await db.athletes.toArray();
};
export const getAthleteById = async (id) => {
    return await db.athletes.get(Number(id)); // Retrieves athlete directly by ID
};
export const deleteAthleteByOid = async (oid) => {
    const athlete = await db.athletes.where("oid").equals(Number(oid)).first();
    if (athlete) {
        await db.athletes.delete(athlete.id);
        await db.athletes_weight.where("athlete").equals(athlete.id).delete();
        await db.athletes_height.where("athlete").equals(athlete.id).delete();
        await db.shooting.where("athlete").equals(athlete.id).delete();
    }
};
export const deleteAthlete = async (id) => {
    const current = await getAthleteById(id);
    await db.athletes.delete(Number(id));
    await db.shooting.where("athlete").equals(Number(id)).delete();

    if (current.oid !== 0) {
        await db.deletedItems.add({
            oid: current.oid,
            table: 'athletes',
            deletedAt: Math.round(Date.now() / 1000)
        });
    }
};

// Shooting
// Function to add an athlete
export const addShooting = async ({ athlete, event, type }) => {
    const timestamp = Math.round(Date.now() / 1000);

    let data = {
        crdate: timestamp,
        tstmp: timestamp,
        date: timestamp,
        position: 'p',
    };

    if (athlete) {
        if (athlete.uid) {
            data.athlete = athlete.uid; // If `athlete` is an object, store `uid`
        } else if (Number.isInteger(athlete)) {
            data.athlete = athlete; // If `athlete` is an integer, store directly
        }
    }

    if (event) {
        if (event.uid) {
            data.event = event.uid; // If `event` is an object, store `uid`
        } else if (Number.isInteger(event)) {
            data.event = event; // If `event` is an integer, store directly
        }
    }

    if (type) {
        data.type = type;
    }

    const id = await db.shooting.add(data);

    return id;
};

export const deleteShootingByOid = async (oid) => {
    const shooting = await db.shooting.where("oid").equals(Number(oid)).first();
    if (shooting) {
        await db.shooting.delete(shooting.id);
    }
};

export const deleteShooting = async (id) => {
    const current = await db.shooting.get(Number(id));
    await db.shooting.delete(Number(id));
    if (current.oid !== 0) {
        await db.deletedItems.add({
            oid: current.oid,
            table: 'shooting',
            deletedAt: Math.round(Date.now() / 1000)
        });
    }
};
export const getShootings = async () => {
    return await db.shooting.toArray();
};
export const getShootingsByAthlete = async (athleteId) => {
    return await db.shooting.where("athlete").equals(Number(athleteId)).toArray();
};
export const getShootingsByAthleteAndEvent = async (athleteId, eventId) => {
    return await db.shooting
        .where({ event: Number(eventId), athlete: Number(athleteId) })
        .toArray();
};
export const getShootingById = async (id) => {
    return await db.shooting.get(Number(id)); // Retrieves athlete directly by ID
};
export const updateShooting = async (id, updatedData) => {
    const timestamp = Math.round(Date.now() / 1000);
    updatedData.tstmp = timestamp;
    const returnValue = await db.shooting.update(Number(id), updatedData);
    return returnValue;
};

// Event
export const addEvent = async (eventData) => {
    const timestamp = Math.round(Date.now() / 1000);

    if (!eventData.crdate) {
        eventData.crdate = timestamp
    }
    if (!eventData.date) {
        eventData.date = timestamp
    }

    eventData.tstmp = timestamp

    delete eventData.id;

    const id = await db.event.add(eventData);

    return id;
};
export const updateEvent = async (id, updatedData) => {
    const timestamp = Math.round(Date.now() / 1000);
    updatedData.tstmp = timestamp;
    return await db.event.update(Number(id), updatedData);
};
export const getEvents = async () => {
    return await db.event.toArray();
};
export const getEventById = async (id) => {
    return await db.event.get(Number(id)); // Retrieves athlete directly by ID
};
export const updateEventAthlete = async (id, updatedData) => {
    const timestamp = Math.round(Date.now() / 1000);
    updatedData.tstmp = timestamp;
    return await db.event_athletes.update(Number(id), updatedData);
};
export const getAthletesByEvent = async (eventId) => {
    const records = await db.event_athletes.where("event").equals(Number(eventId)).toArray();
    const mergedAthletes = await Promise.all(
        records.map(async (record) => {
            const athlete = await getAthleteById(record.athlete);
            return athlete
                ? {
                    ...athlete,
                    eventInfo: record,
                }
                : null;
        })
    );

    return mergedAthletes;
};
export const getAthleteByIdAndEvent = async (eventId, athleteId) => {
    const record = await db.event_athletes
        .where({
            event: Number(eventId),
            athlete: Number(athleteId)
        })
        .first(); // returns only the first match

    if (!record) return null;

    const athlete = await getAthleteById(record.athlete);
    if (!athlete) return null;

    return {
        ...athlete,
        eventInfo: record,
    };
};
export const linkAthleteToEvent = async (athleteId, eventId) => {
    const existing = await db.event_athletes
        .where({ athlete: Number(athleteId), event: Number(eventId) })
        .first();

    if (!existing) {
        await db.event_athletes.add({
            athlete: Number(athleteId),
            event: Number(eventId),
            crdate: Math.floor(Date.now() / 1000),
        });
    }
};
export const getEventsByAthlete = async (athleteId) => {
    const records = await db.event_athletes.where("athlete").equals(Number(athleteId)).toArray();
    return records.map(record => record.event);
};
export const unlinkAthleteFromEvent = async (athleteId, eventId) => {
    try {
        const current = await db.event_athletes
            .where({ athlete: Number(athleteId), event: Number(eventId) })
            .first();

        if (current.oid) {
            await db.deletedItems.add({
                oid: current.oid,
                table: 'event_athlete',
                deletedAt: Math.round(Date.now() / 1000)
            });
        }
        // ✅ Step 1: Delete the athlete-event link
        await db.event_athletes
            .where({ event: Number(eventId), athlete: Number(athleteId) })
            .delete();

        // ✅ Step 2: Find shootings for the athlete in this event
        const relatedShootings = await db.shooting
            .where({ event: Number(eventId), athlete: Number(athleteId) })
            .toArray();

        // ✅ Step 3: Unassign event from those shootings
        const updatedShootings = relatedShootings.map((s) => ({
            ...s,
            event: null
        }));

        // ✅ Step 4: Save updated shootings
        await Promise.all(
            updatedShootings.map((s) => db.shooting.update(s.id, { event: null }))
        );

        return true;
    } catch (error) {
        console.error("Error unlinking athlete from event:", error);
        return false;
    }
};

export const deleteEventByOid = async (oid) => {
    const shooting = await db.event.where("oid").equals(Number(oid)).first();
    if (shooting) {
        await db.event.delete(shooting.id);
    }
};
export const deleteEventAthleteByOid = async (oid) => {
    const shooting = await db.event_athletes.where("oid").equals(Number(oid)).first();
    if (shooting) {
        await db.event_athletes.delete(shooting.id);
    }
};

export const deleteEvent = async (id) => {
    const current = await db.event.get(Number(id));
    await db.event.delete(Number(id));

    if (current.oid !== 0) {
        await db.deletedItems.add({
            oid: current.oid,
            table: 'event',
            deletedAt: Math.round(Date.now() / 1000)
        });
    }
};

export const getLocalChanges = async (table, lastSync) => {
    return db[table]
        .where('tstmp')
        .above(lastSync)
        .toArray();
};

export const getDeletedOids = async (table) => {
    return db.deleted.where('table').equals(table).toArray().then(items => items.map(i => i.oid));
};

async function applyServerData(table, serverData, idMap) {
    for (const item of serverData) {
        const existing = await db[table].get({ uid: item.uid });

        if (!existing || item.tstamp > existing.tstamp) {
            await db[table].put({ ...item, synced: true });
        }
    }

    // Replace local oids with real uids if needed
    for (const [oid, uid] of Object.entries(idMap)) {
        await mapOidToUid(table, oid, uid);
    }
}

async function mapOidToUid(table, oid, uid) {
    const item = await db[table].get({ oid });
    if (item) {
        delete item.oid;
        item.uid = uid;
        await db[table].put(item);
    }
}
export default db;
